import React, { useState } from "react";
import {
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Box,
    Alert,
    LinearProgress,
    Fade,
    Stack
} from "@mui/material";
import {
    Key as KeyIcon,
    TableRestaurant as TableIcon,
    Restaurant as MenuIcon,
    CheckCircle as SuccessIcon
} from "@mui/icons-material";
import servicesProvider from "../../../services/ServicesProvider";

interface CassaincloudDialogProps {
    ristoranteId: string;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type StepType = "api-key" | "tables" | "menu" | "complete";

interface TutorialStep {
    id: StepType;
    label: string;
    description: string;
    icon: React.ReactNode;
    isProcessing?: boolean;
    isCompleted?: boolean;
}

const CassaincloudDialog: React.FC<CassaincloudDialogProps> = ({
    ristoranteId,
    open,
    onClose,
    onSuccess
}) => {
    const [apiKey, setApiKey] = useState("");
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
    const [tokenInfo, setTokenInfo] = useState<any>(null);
    const [syncResults, setSyncResults] = useState<{
        tables?: any;
        menu?: any;
    }>({});

    const steps: TutorialStep[] = [
        {
            id: "api-key",
            label: "API Key",
            description: "Inserisci la tua API Key di Cassa in Cloud per iniziare l'integrazione",
            icon: <KeyIcon />
        },
        {
            id: "tables",
            label: "Scarica Tavoli",
            description: "Download della configurazione tavoli da Cassa in Cloud",
            icon: <TableIcon />
        },
        {
            id: "menu",
            label: "Sincronizza Menu",
            description: "Importa prodotti e categorie dal sistema Cassa in Cloud",
            icon: <MenuIcon />
        },
        {
            id: "complete",
            label: "Completato",
            description: "Integrazione completata con successo!",
            icon: <SuccessIcon />
        }
    ];

    const currentStep = steps[currentStepIndex];
    const isProcessingStep = currentStep.id === "tables" || currentStep.id === "menu";

    const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setApiKey(event.target.value);
        setError("");
    };

    const executeCurrentStep = async () => {
        setLoading(true);
        setError("");

        try {
            switch (currentStep.id) {
                case "api-key":
                    const result = await servicesProvider
                        .getCassaincloudService()
                        .insertRistoranteCassaincloudWithApiKey(ristoranteId, apiKey);

                    // Salva informazioni del token per il riepilogo finale
                    if (result) {
                        setTokenInfo(result);
                    }
                    break;

                case "tables":
                    const tablesResult = await servicesProvider
                        .getCassaincloudService()
                        .insertCassaincloudTavoloList(ristoranteId);

                    setSyncResults(prev => ({
                        ...prev,
                        tables: tablesResult
                    }));
                    break;

                case "menu":
                    const menuResult = await servicesProvider
                        .getCassaincloudService()
                        .insertCassaincloudMenu(ristoranteId);

                    setSyncResults(prev => ({
                        ...prev,
                        menu: menuResult
                    }));
                    break;
            }

            setCompletedSteps(prev => new Set(prev).add(currentStep.id));
            setLoading(false);

            // Se è l'ultimo step operativo (menu), passa al completamento
            if (currentStep.id === "menu") {
                setCurrentStepIndex(3); // Indice dello step "complete"
                setCompletedSteps(prev => new Set(prev).add("complete"));
            }
        } catch (error: any) {
            setLoading(false);
            setError(error.message || "Errore durante l'operazione. Verifica i dati e riprova.");
            console.error("Errore step:", error);
        }
    };

    const handleNext = async () => {
        if (currentStep.id === "api-key") {
            if (!apiKey.trim()) {
                setError("L'API Key è richiesta");
                return;
            }
            await executeCurrentStep();
        } else if (currentStep.id === "complete") {
            // Chiudi il dialog quando si clicca su "Completa"
            handleComplete();
        } else {
            // Per tutti gli altri step, esegui l'operazione specifica
            await executeCurrentStep();
        }
    };

    const handleComplete = () => {
        onSuccess();
        onClose();

        // Reset stato per future aperture
        resetDialogState();
    };

    const resetDialogState = () => {
        setApiKey("");
        setCurrentStepIndex(0);
        setCompletedSteps(new Set());
        setTokenInfo(null);
        setSyncResults({});
        setError("");
    };

    const handleClose = () => {
        if (loading) {
            return;
        }
        onClose();
        resetDialogState();
    };

    const getStepButtonText = () => {
        switch (currentStep.id) {
            case "api-key":
                return "Collega Account";
            case "tables":
                return "Scarica Tavoli";
            case "menu":
                return "Sincronizza Menu";
            default:
                return "Avanti";
        }
    };

    return (
        <>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">Integrazione con Cassa in Cloud</Typography>
                    {loading && !error && <CircularProgress size={20} />}
                </Box>
            </DialogTitle>

            <DialogContent>
                <Stack gap={3}>
                    {/* Stepper */}
                    <Box>
                        <Stepper activeStep={currentStepIndex} alternativeLabel>
                            {steps.map((step, index) => (
                                <Step key={step.id} completed={completedSteps.has(step.id)}>
                                    <StepLabel>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: 1 }}>
                                            {step.icon}
                                            <Typography variant="body2">{step.label}</Typography>
                                        </Box>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    {/* Barra di progresso durante l'elaborazione */}
                    {loading && isProcessingStep && (
                        <Box sx={{ mb: 3 }}>
                            <LinearProgress />
                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                {currentStep.id === "tables" ? "Sincronizzazione tavoli..." :
                                    currentStep.id === "menu" ? "Sincronizzazione menu..." :
                                        "Elaborazione in corso..."}
                            </Typography>
                        </Box>
                    )}

                    {/* Contenuto del passo corrente */}
                    <Fade in={true} key={currentStep.id}>
                        <Stack gap={2}>
                            <DialogContentText>
                                {currentStep.description}
                            </DialogContentText>

                            {/* Contenuto specifico per ogni passo */}
                            {currentStep.id === "api-key" && (
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="API Key di Cassa in Cloud"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={apiKey}
                                    onChange={handleApiKeyChange}
                                    helperText="Inserisci l'API Key fornita da Cassa in Cloud per il tuo ristorante"
                                    disabled={loading}
                                    placeholder="Inserisci qui la tua API Key..."
                                />
                            )}

                            {currentStep.id === "tables" && !completedSteps.has("tables") && (
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        Premi "Scarica Tavoli" per scaricare la configurazione dei tavoli da Cassa in Cloud.
                                    </Typography>
                                </Alert>
                            )}

                            {currentStep.id === "menu" && !completedSteps.has("menu") && (
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        Premi "Sincronizza Menu" per importare prodotti e categorie da Cassa in Cloud.
                                        Questo creerà automaticamente sezioni e articoli nel tuo sistema.
                                    </Typography>
                                </Alert>
                            )}

                            {/* Risultati delle operazioni completate */}
                            {completedSteps.has("tables") && syncResults.tables && currentStep.id === "tables" && (
                                <Alert severity="success">
                                    <Typography variant="body2">
                                        <strong>Tavoli sincronizzati:</strong> {syncResults.tables.count || 0} tavoli importati con successo.
                                    </Typography>
                                </Alert>
                            )}

                            {completedSteps.has("menu") && syncResults.menu && currentStep.id === "menu" && (
                                <Alert severity="success">
                                    <Typography variant="body2">
                                        <strong>Menu sincronizzato:</strong> Prodotti e categorie importati con successo da Cassa in Cloud.
                                    </Typography>
                                </Alert>
                            )}

                            {completedSteps.has(currentStep.id) && currentStep.id !== "complete" && (
                                !syncResults.tables && !syncResults.menu && (
                                    <Alert severity="success">
                                        <Typography variant="body2">
                                            Operazione completata con successo!
                                        </Typography>
                                    </Alert>
                                )
                            )}

                            {currentStep.id === "complete" && (
                                <>
                                    <Alert severity="success">
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            Integrazione completata!
                                        </Typography>
                                        <Typography variant="body2">
                                            L'integrazione con Cassa in Cloud è stata completata con successo!
                                            Tutti i dati sono stati sincronizzati correttamente.
                                        </Typography>
                                    </Alert>

                                    {/* Riepilogo risultati */}
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Riepilogo sincronizzazione:
                                        </Typography>

                                        {syncResults.tables && (
                                            <Typography variant="body2" color="text.secondary">
                                                • Tavoli: <strong>{syncResults.tables.count || 0} importati</strong>
                                            </Typography>
                                        )}

                                        {syncResults.menu && (
                                            <Typography variant="body2" color="text.secondary">
                                                • Menu: <strong>Sincronizzato con successo</strong>
                                            </Typography>
                                        )}

                                        {tokenInfo && (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    • Status: <strong>{tokenInfo.status || 'Attivo'}</strong>
                                                </Typography>
                                                {tokenInfo.salesPointId && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        • Sales Point ID: <strong>{tokenInfo.salesPointId}</strong>
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </Box>

                                    <Alert severity="info">
                                        <Typography variant="body2">
                                            <strong>Funzionalità attive:</strong><br />
                                            • Gestione automatica dei token<br />
                                            • Sincronizzazione tavoli completata<br />
                                            • Sincronizzazione menu completata<br />
                                            • Sistema pronto per l'utilizzo
                                        </Typography>
                                    </Alert>
                                </>
                            )}

                            {error && (
                                <Alert severity="error">
                                    {error}
                                </Alert>
                            )}
                        </Stack>
                    </Fade>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                >
                    {currentStep.id === "complete" ? "Chiudi" : "Annulla"}
                </Button>

                {currentStep.id !== "complete" && !completedSteps.has(currentStep.id) && (
                    <Button
                        onClick={handleNext}
                        variant="contained"
                        disabled={loading || (currentStep.id === "api-key" && !apiKey.trim())}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? "Elaborando..." : getStepButtonText()}
                    </Button>
                )}

                {completedSteps.has(currentStep.id) && currentStep.id !== "complete" && currentStepIndex < steps.length - 1 && (
                    <Button
                        onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
                        variant="contained"
                        disabled={loading}
                    >
                        Prossimo Passo
                    </Button>
                )}

                {currentStep.id === "complete" && (
                    <Button
                        onClick={handleComplete}
                        variant="contained"
                        color="primary"
                    >
                        Completa
                    </Button>
                )}
            </DialogActions>
        </>
    );
};

export default CassaincloudDialog;